// Settings › Issue grouping (Console D, APS-319): the one rule every project groups by (D51, with
// the app always in the fingerprint since D104; crates/symbolicator/src/grouping.rs), where
// merging happens (D102), and the project's in-app code (D80 addendum, APS-473): per kind of
// frame, what the team counts as its own and what it never blames, which owners and admins edit.
// Fingerprint rules and a preview have no API yet.
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useId, useState } from 'react'
import { isApiError } from '../api/client'
import { inAppListsQuery, orgQuery, projectsQuery, useSetInAppLists } from '../api/queries'
import type { InAppKind, InAppList } from '../api/types'
import { PageHead, ScrollX, Section, useToast } from '../components/d'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'

/** The rule's version, recorded on every occurrence (grouping.rs GROUPING_VERSION). */
export const GROUPING_VERSION = 2

/** The most entries a list holds, and characters an entry (the API's, accounts in-app-lists.ts). */
const MAX_ENTRIES = 50
const MAX_LENGTH = 200

/** The kinds of frame, in the API's order: the row's name, what an entry is, and examples. */
const KINDS: readonly {
  kind: InAppKind
  name: MessageId
  how: MessageId
  bad: MessageId
  examples: { in_app: string; third_party: string }
  /** The entry's shape, as the API checks it. */
  shape: (entry: string) => boolean
}[] = [
  {
    kind: 'apple',
    name: 'settings.grouping.kind.apple',
    how: 'settings.grouping.how.apple',
    bad: 'settings.grouping.bad.apple',
    examples: { in_app: 'CheckoutKit', third_party: 'Analytics' },
    shape: (e) => !e.includes('/'),
  },
  {
    kind: 'jvm',
    name: 'settings.grouping.kind.jvm',
    how: 'settings.grouping.how.jvm',
    bad: 'settings.grouping.bad.jvm',
    examples: { in_app: 'com.acme.shop', third_party: 'com.google' },
    shape: (e) => /^[\p{L}_$][\p{L}\p{N}_$]*(\.[\p{L}_$][\p{L}\p{N}_$]*)*$/u.test(e),
  },
  {
    kind: 'native',
    name: 'settings.grouping.kind.native',
    how: 'settings.grouping.how.native',
    bad: 'settings.grouping.bad.native',
    examples: { in_app: 'libcheckout.so', third_party: 'libvendor.so' },
    shape: (e) => /^[^\s/\\]+$/.test(e),
  },
  {
    kind: 'javascript',
    name: 'settings.grouping.kind.javascript',
    how: 'settings.grouping.how.javascript',
    bad: 'settings.grouping.bad.javascript',
    examples: { in_app: '@acme/ui', third_party: 'src/vendor/' },
    shape: (e) =>
      /^(@[a-z0-9~-][a-z0-9._~-]*\/)?[a-z0-9~-][a-z0-9._~-]*$/.test(e) ||
      (e.endsWith('/') && !e.includes('\\') && !/(^|\/)\.\.?\//.test(e)),
  },
]

/** Characters the API refuses in an entry: controls, zero-width and text direction ones. */
// biome-ignore lint/suspicious/noControlCharactersInRegex: control characters are what it refuses
const UNSAFE = /[\u0000-\u001f\u007f-\u009f​-‏‪-‮⁠⁦-⁩﻿]/

type ListName = 'in_app' | 'third_party'
type Draft = Record<InAppKind, Record<ListName, string>>
/** A refused list, by `<kind>.<list>`: the message key and its values. */
type Problems = Partial<Record<`${InAppKind}.${ListName}`, [MessageId, Record<string, string>]>>

/** A textarea's entries: one a line, trimmed, blank lines dropped. */
export function entries(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '')
}

function draftOf(lists: InAppList[]): Draft {
  const draft = {} as Draft
  for (const { kind } of KINDS) {
    const l = lists.find((x) => x.kind === kind)
    draft[kind] = {
      in_app: (l?.in_app ?? []).join('\n'),
      third_party: (l?.third_party ?? []).join('\n'),
    }
  }
  return draft
}

/** A list's entries as the API takes them: a package typed as the table shows it is the package. */
const listOf = (kind: InAppKind, text: string) => [
  ...new Set(entries(text).map((e) => (kind === 'jvm' ? e.replace(/\.\*$/, '') : e))),
]

/** The kinds whose lists the draft changes, as the API takes them. */
export function changedLists(lists: InAppList[], draft: Draft) {
  const same = (a: string[], b: string[]) => a.length === b.length && a.every((e, i) => e === b[i])
  return KINDS.flatMap(({ kind }) => {
    const l = lists.find((x) => x.kind === kind)
    const in_app = listOf(kind, draft[kind].in_app)
    const third_party = listOf(kind, draft[kind].third_party)
    return same(in_app, l?.in_app ?? []) && same(third_party, l?.third_party ?? [])
      ? []
      : [{ kind, in_app, third_party }]
  })
}

/** What the API would refuse in a draft, per list, so each error sits under its textarea. */
export function problems(draft: Draft): Problems {
  const out: Problems = {}
  for (const { kind, bad, shape } of KINDS) {
    const lists = {
      in_app: listOf(kind, draft[kind].in_app),
      third_party: listOf(kind, draft[kind].third_party),
    }
    for (const list of ['in_app', 'third_party'] as const) {
      const items = lists[list]
      const unsafe = items.find((e) => e.length > MAX_LENGTH || UNSAFE.test(e))
      const misshapen = items.find((e) => !shape(e))
      const both = list === 'third_party' ? items.find((e) => lists.in_app.includes(e)) : undefined
      const found: [MessageId, Record<string, string>] | undefined =
        unsafe !== undefined
          ? ['settings.grouping.bad.chars', { entry: unsafe.slice(0, 60) }]
          : misshapen !== undefined
            ? [bad, { entry: misshapen }]
            : items.length > MAX_ENTRIES
              ? ['settings.grouping.bad.many', { count: String(items.length) }]
              : both !== undefined
                ? ['settings.grouping.bad.both', { entry: both }]
                : undefined
      if (found) out[`${kind}.${list}`] = found
    }
  }
  return out
}

/** An entry as the table reads it: a JVM package with its subpackages, as `com.acme.shop.*`. */
export const shownEntry = (kind: InAppKind, entry: string) =>
  kind === 'jvm' ? `${entry}.*` : entry

function Entries({ kind, items }: { kind: InAppKind; items: string[] }) {
  if (items.length === 0) return <span className="ia-none">–</span>
  return (
    <span translate="no">
      {items.map((e, i) => (
        <span key={e}>
          {i > 0 ? ' · ' : null}
          <code>{shownEntry(kind, e)}</code>
        </span>
      ))}
    </span>
  )
}

/** "In-app code": the project's lists, read by members, edited by owners and admins. */
function InAppCode({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t } = useI18n()
  const lists = useQuery(inAppListsQuery(orgId, projectId))
  const org = useQuery(orgQuery(orgId))
  const save = useSetInAppLists(orgId, projectId)
  const { toast, show } = useToast()
  const [draft, setDraft] = useState<Draft | null>(null)
  const [refused, setRefused] = useState<Problems>({})
  const ids = useId()
  const canEdit = org.data?.role === 'owner' || org.data?.role === 'admin'
  const current = lists.data?.lists ?? []
  const changes = draft ? changedLists(current, draft) : []

  function close() {
    setDraft(null)
    setRefused({})
    save.reset()
  }

  function submit() {
    if (!draft || changes.length === 0) return
    const found = problems(draft)
    setRefused(found)
    if (Object.keys(found).length > 0) return
    save.mutate(changes, {
      onSuccess: () => {
        close()
        show(t('settings.grouping.saved'))
      },
    })
  }

  const cell = (kind: InAppKind, list: ListName, label: string) => {
    if (!draft)
      return <Entries kind={kind} items={current.find((l) => l.kind === kind)?.[list] ?? []} />
    const problem = refused[`${kind}.${list}`]
    const errorId = `${ids}-${kind}-${list}-error`
    return (
      <>
        <textarea
          className="input mono"
          rows={3}
          translate="no"
          spellCheck={false}
          aria-label={label}
          aria-describedby={problem ? `${ids}-${kind}-how ${errorId}` : `${ids}-${kind}-how`}
          aria-invalid={problem ? true : undefined}
          placeholder={KINDS.find((k) => k.kind === kind)?.examples[list]}
          value={draft[kind][list]}
          onChange={(e) => {
            setDraft({ ...draft, [kind]: { ...draft[kind], [list]: e.target.value } })
            if (problem) {
              const { [`${kind}.${list}` as const]: _, ...rest } = refused
              setRefused(rest)
            }
          }}
        />
        {problem ? (
          <p id={errorId} role="alert" className="ia-err" translate="no">
            {t(problem[0], problem[1])}
          </p>
        ) : null}
      </>
    )
  }

  const nothing = changes.length === 0
  return (
    <Section
      className={draft ? 'in-app editing' : 'in-app'}
      title={t('settings.grouping.inApp')}
      meta={t('settings.grouping.inAppSub')}
      right={
        canEdit && lists.data ? (
          draft ? (
            <span className="ia-acts">
              <button
                type="button"
                className="btn ghost sm"
                disabled={save.isPending}
                onClick={close}
              >
                {t('settings.grouping.cancel')}
              </button>
              <button
                type="button"
                className={nothing ? 'btn sm' : 'btn solid sm'}
                disabled={save.isPending || nothing}
                title={nothing ? t('settings.grouping.nothing') : undefined}
                onClick={submit}
              >
                {t('settings.grouping.save')}
              </button>
            </span>
          ) : (
            <button type="button" className="btn sm" onClick={() => setDraft(draftOf(current))}>
              {t('settings.grouping.edit')}
            </button>
          )
        ) : undefined
      }
    >
      {lists.isPending ? (
        <Loading />
      ) : lists.isError ? (
        <ErrorState error={lists.error} retry={() => lists.refetch()} />
      ) : (
        <>
          <ScrollX label={t('table.of', { name: t('settings.grouping.inApp') })}>
            <table className="t" aria-label={t('settings.grouping.inApp')}>
              <thead>
                <tr>
                  <th className="f" scope="col">
                    {t('settings.grouping.col.platform')}
                  </th>
                  <th scope="col">{t('settings.grouping.col.inApp')}</th>
                  <th className="last" scope="col">
                    {t('settings.grouping.col.thirdParty')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {KINDS.map(({ kind, name, how }) => (
                  <tr key={kind}>
                    <td className="f">
                      {t(name)}
                      {draft ? (
                        <span id={`${ids}-${kind}-how`} className="t2">
                          {t(how)}
                        </span>
                      ) : null}
                    </td>
                    <td data-label={t('settings.grouping.col.inApp')}>
                      {cell(kind, 'in_app', `${t('settings.grouping.col.inApp')}, ${t(name)}`)}
                    </td>
                    <td className="last" data-label={t('settings.grouping.col.thirdParty')}>
                      {cell(
                        kind,
                        'third_party',
                        `${t('settings.grouping.col.thirdParty')}, ${t(name)}`,
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
          <div className="se-hint" style={{ paddingTop: 10 }}>
            <p>{t('settings.grouping.inAppHint')}</p>
            <p>{t('settings.grouping.inAppApplies')}</p>
          </div>
          {save.error ? (
            <p role="alert" className="ia-err">
              {isApiError(save.error, 400) ? (
                <>
                  {t('settings.grouping.invalid')} <span translate="no">{save.error.message}</span>
                </>
              ) : isApiError(save.error, 403) ? (
                t('settings.adminOnly')
              ) : (
                t('state.failed')
              )}
            </p>
          ) : null}
        </>
      )}
      {toast}
    </Section>
  )
}

export function GroupingSettings({
  orgId,
  projectId,
  crumb,
  title,
}: {
  orgId: string
  projectId: string
  crumb: string
  title: string
}) {
  const { t } = useI18n()
  const projects = useQuery(projectsQuery(orgId))
  const project = projects.data?.find((p) => p.id === projectId)
  const version = `v${GROUPING_VERSION}`
  return (
    <main id="main" className="pg se-pg grouping">
      <PageHead
        crumb={crumb}
        title={title}
        meta={
          <>
            {project ? <span translate="no">{project.name}</span> : null}
            {project ? ' · ' : null}
            <span translate="no">{t('settings.grouping.meta', { version })}</span>
          </>
        }
      />
      <div className="grid2">
        <div className="sections">
          <Section
            title={t('settings.grouping.default')}
            meta={t('settings.grouping.defaultSub', { version })}
          >
            <dl className="kv se-kv">
              <dt>{t('settings.grouping.crashes')}</dt>
              <dd>{t('settings.grouping.crashesRule')}</dd>
              <dt>{t('settings.grouping.errors')}</dt>
              <dd>{t('settings.grouping.errorsRule')}</dd>
              <dt>{t('settings.grouping.hangs')}</dt>
              <dd>{t('settings.grouping.hangsRule')}</dd>
              <dt>{t('settings.grouping.exits')}</dt>
              <dd>{t('settings.grouping.exitsRule')}</dd>
              <dt>{t('settings.grouping.app')}</dt>
              <dd>{t('settings.grouping.appRule')}</dd>
              <dt>{t('settings.grouping.lines')}</dt>
              <dd>{t('settings.grouping.linesRule')}</dd>
            </dl>
            <p className="se-hint" style={{ paddingTop: 10 }}>
              {t('settings.grouping.detail')} {t('settings.grouping.symbols')}
            </p>
          </Section>
          <InAppCode orgId={orgId} projectId={projectId} />
        </div>
        <aside className="sections" aria-label={t('settings.grouping.side')}>
          <Section title={t('settings.grouping.merging')}>
            <p className="se-p">{t('settings.grouping.mergingBody')}</p>
            <p className="se-hint" style={{ paddingTop: 10 }}>
              <Link className="ul" to="/o/$orgId/p/$projectId/issues" params={{ orgId, projectId }}>
                {t('nav.issues')}
              </Link>
            </p>
          </Section>
          <Section title={t('settings.grouping.version')} minor>
            <p className="se-p">{t('settings.grouping.versionBody')}</p>
          </Section>
        </aside>
      </div>
    </main>
  )
}
